import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { createMyBankAccount } from "../../api/user/createMyBankAccount";
import { getMyBankAccount } from "../../api/user/getMyBankAccount";
import { getMyContact } from "../../api/user/getMyContact";
import BackButton from "../../components/buttons/BackButton";
import HomeButton from "../../components/buttons/HomeButton";
import APCheckbox from "../../components/form/APCheckbox";
import APForm from "../../components/form/APForm";
import APTextField from "../../components/form/APTextField";
import PageContainer from "../../components/PageContainer";
import { downloadFile } from "../../helpers/download";
import { trimStringInputs } from "../../helpers/form";
import { useApi } from "../../providers/ApiProvider";
import { useAuth } from "../../providers/AuthProvider";
import { ACTION_CONFIRMATION_EVENT_IDS } from "../../types/Dossier";

type Inputs = {
  financialInstitution: string;
  accountName: string;
  bsb: string;
  accountNumber: string;
  agree: boolean;
};

const DirectDebit = () => {
  const { dossierId } = useParams();
  const [searchParams] = useSearchParams();
  const [navigationCheckComplete, setNavigationCheckComplete] = useState(false);
  const { fetchWithAuth } = useApi();
  const { getScopedPageUrl } = useAuth();
  const shouldLoadBankAccount = !dossierId;
  const redirectOnSubmit = searchParams.get("redirectOnSubmit");
  const fromConfirmation = searchParams.get("fromConfirmation") === "true";
  let destination;

  if (redirectOnSubmit) {
    destination = redirectOnSubmit;
  } else if (dossierId) {
    destination = getScopedPageUrl(
      "dossierActionConfirmation",
      dossierId,
      ACTION_CONFIRMATION_EVENT_IDS.DIRECT_DEBIT
    );
  } else {
    destination = "/dossiers";
  }

  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<Inputs>({ mode: "onTouched" });
  const { mutateAsync: mutateAsyncUpdate, isLoading: isLoadingUpdate } =
    createMyBankAccount(dossierId, {
      mutationOptions: {
        successNavPath: destination,
        successReplaceHistory: !!redirectOnSubmit,
      },
    });

  const contact = getMyContact();
  const bankAccount = getMyBankAccount({ disabled: !shouldLoadBankAccount });

  if (contact.isError) {
    throw contact.error;
  }

  if (bankAccount.isError) {
    throw bankAccount.error;
  }

  const isLoading = contact.isLoading || bankAccount.isLoading;
  const isSuccess =
    contact.isSuccess && (!shouldLoadBankAccount || bankAccount.isSuccess);

  const onSubmit: SubmitHandler<Inputs> = async (_data) => {
    trimStringInputs(_data, setValue);
    const data = getValues();

    mutateAsyncUpdate({
      financialInstitution: data.financialInstitution,
      accountName: data.accountName,
      bsb: data.bsb,
      accountNumber: data.accountNumber,
      driverDevice: navigator.userAgent,
    });
  };

  useEffect(() => {
    if (isSuccess) {
      // If contact already has an active bank account then redirect to no next action page
      if (contact.data.activeBankAccountSetup && dossierId) {
        navigate(
          getScopedPageUrl("dossierActionConfirmation", dossierId, "not-ready"),
          {
            replace: true,
          }
        );
      } else {
        setNavigationCheckComplete(true);
      }
    }
  }, [isSuccess]);

  const downloadPDF = async () => {
    const pdf = await fetchWithAuth<Blob>("getDirectDebitTermsPDF", dossierId, {
      responseOptions: { responseType: "blob" },
    });

    downloadFile(pdf, "DirectDebitTerms.pdf");
  };

  return (
    <PageContainer
      loading={isLoading || !navigationCheckComplete}
      title="Bank Details"
    >
      {isSuccess && (
        <Stack spacing={2}>
          <Typography variant="labelLarge">
            We need your bank details to reimburse you for vehicle expenses you
            may incur.
            <br />
            <br />
            We also setup a direct debit to be used in the unlikely event that
            we cannot recover vehicle expenses from your employer.
          </Typography>
          {bankAccount.data && (
            <Typography
              variant="titleMedium"
              color="secondary"
              align="center"
              sx={{ px: 1 }}
            >
              Click Save to replace existing account
              <br />
              BSB: {bankAccount.data.bsb.replace("-", "")} Account:{" "}
              {bankAccount.data.accountNumber}
            </Typography>
          )}
          <APForm
            onSubmit={handleSubmit(onSubmit)}
            submitText="Next"
            submitIcon={<PlayArrowOutlined />}
            leftButton={
              fromConfirmation ? (
                <HomeButton destination="dossierHome" dossierId={dossierId} />
              ) : (
                <BackButton />
              )
            }
            isLoading={isLoadingUpdate}
            isError={Object.keys(errors).length > 0}
          >
            <APTextField<Inputs>
              name="financialInstitution"
              label="Financial Institution"
              control={control}
              validations={{
                required: true,
                maxLength: 100,
              }}
              errors={errors}
              defaultValue=""
            />

            <APTextField<Inputs>
              name="accountName"
              label="Account Name"
              control={control}
              validations={{
                required: true,
                maxLength: 100,
              }}
              errors={errors}
              defaultValue=""
            />

            <APTextField<Inputs>
              name="bsb"
              label="BSB"
              control={control}
              validations={{
                required: true,
                formatValidation: /\d{3}-?\d{3}/,
              }}
              errors={errors}
              defaultValue=""
              placeholder="xxx-xxx"
            />

            <APTextField<Inputs>
              name="accountNumber"
              label="Account Number"
              control={control}
              validations={{
                required: true,
                maxLength: 20,
                formatValidation: /\d+/,
              }}
              errors={errors}
              defaultValue=""
            />

            <Box>
              <Button
                size="large"
                color="primary"
                variant="outlined"
                disableElevation={true}
                onClick={() => downloadPDF()}
              >
                Direct Debit Terms
              </Button>
            </Box>

            <Box>
              <Box>
                <APCheckbox
                  name="agree"
                  control={control}
                  label="I agree to the terms of the Direct Debit."
                  defaultValue={false}
                  validations={{ required: true }}
                  errors={errors}
                />
              </Box>
            </Box>
          </APForm>
        </Stack>
      )}
    </PageContainer>
  );
};

export default DirectDebit;
